import { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import styles from './ChiefComplaints.module.css'
import {
  createChiefComplaints,
  getChiefComplaints,
  updateChiefComplaints,
} from '../../../services/CampManagementService'
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  MenuItem,
  Typography,
  Button,
} from '@mui/material'
import {
  COMPLAINTS,
  chiefComplaintsConstants,
  days,
  DURATION_OPTIONS,
  hours,
  moreThanDays,
  textHours,
  TEXT_VISIT_ID,
} from '../constants/Constants'

type Complaint = {
  id: string
  name: string
  selected: boolean
  duration?: number
  durationUnit?: 'hours'
}

// Styles are now in ChiefComplaints.css

interface AppointmentData {
  visitId?: string
  uhid?: string
  campId?: string
}

interface ChiefComplaintsProps {
  appointmentData?: AppointmentData
}

interface ChiefComplaintResponse {
  data: {
    id: string
    patientUhid: string
    visitId: string
    campId: string
    complaints: Array<{
      name: string
      duration: string
      otherDetails: string
    }>
    additionalComments?: string
  }
  success: boolean
  message: string
}

const ChiefComplaints = ({ appointmentData }: ChiefComplaintsProps) => {
  const [comment, setComment] = useState('')
  const [complaints, setComplaints] = useState<Complaint[]>(
    COMPLAINTS?.map((complaint, index) => ({
      id: `complaint-${index}`,
      name: complaint,
      selected: false,
    }))
  )

  const handleComplaintChange =
    (id: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setComplaints((prev) =>
        prev?.map((complaint) =>
          complaint?.id === id
            ? {
                ...complaint,
                selected: event?.target?.checked,
                duration: event?.target?.checked ? 1 : undefined,
                durationUnit: 'hours',
              }
            : complaint
        )
      )
    }

  const handleDurationChange = (id: string, value: number) => {
    setComplaints((prev) =>
      prev?.map((complaint) =>
        complaint?.id === id ? { ...complaint, duration: value } : complaint
      )
    )
  }

  const handleCustomDurationChange = (id: string, value: string) => {
    const numValue = parseInt(value)
    if (!isNaN(numValue)) {
      setComplaints((prev) =>
        prev?.map((complaint) =>
          complaint?.id === id
            ? { ...complaint, duration: numValue, durationUnit: 'hours' }
            : complaint
        )
      )
    }
  }

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment(event?.target?.value)
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [chiefComplaintId, setChiefComplaintId] = useState<string | null>(null)

  const fetchComplaints = useCallback(async () => {
    try {
      setIsLoading(true)
      const visitId = appointmentData?.visitId

      if (!visitId) {
        throw new Error(TEXT_VISIT_ID)
      }
      const response: ChiefComplaintResponse = await getChiefComplaints(visitId)

      if (response?.data) {
        // Store the chief complaint ID for future updates
        if (response?.data?.id) {
          setChiefComplaintId(response?.data?.id)
        }

        // Update the complaints state with the fetched data
        if (response?.data?.complaints) {
          setComplaints((prevComplaints) => {
            return prevComplaints?.map((complaint) => {
              const existingComplaint = response?.data?.complaints?.find(
                (c: any) => c?.name === complaint?.name
              )

              if (existingComplaint) {
                const durationValue =
                  existingComplaint.duration?.toLowerCase()?.trim() || ''
                const hoursMatch = durationValue.match(/(\d+)\s*hours?/i)
                const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 24

                const duration = hours >= 72 ? 3 : hours >= 48 ? 2 : 1

                return {
                  ...complaint,
                  selected: true,
                  duration: duration,
                }
              }
              return complaint
            })
          })
        }

        // Set the additional comments if any
        if (response?.data?.additionalComments) {
          setComment(response?.data?.additionalComments)
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load complaints'
      toast.error(`Error: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }, [
    appointmentData,
    setChiefComplaintId,
    setComplaints,
    setComment,
    setIsLoading,
  ])

  // Fetch existing complaints on component mount
  useEffect(() => {
    if (appointmentData?.visitId) {
      fetchComplaints()
    }
  }, [appointmentData, fetchComplaints])

  const handleSubmit = async () => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      const payload = {
        patientUhid: appointmentData?.uhid || '',
        visitId: appointmentData?.visitId || '',
        campId: appointmentData?.campId || '',
        complaints: complaints
          ?.filter((c) => c?.selected)
          ?.map((complaint) => ({
            name: complaint?.name,
            duration:
              complaint?.duration === 1
                ? hours
                : complaint?.duration === 2
                ? days
                : complaint?.duration === 3
                ? moreThanDays
                : hours,
            otherDetails: '',
          })),
        additionalComments: comment?.trim(),
      }

      let response
      if (chiefComplaintId) {
        // Update existing chief complaint
        response = await updateChiefComplaints(payload, chiefComplaintId)
        toast.success(response?.message)
      } else {
        // Create new chief complaint
        response = await createChiefComplaints(payload)
        // Store the ID for future updates
        if (response?.data?.id) {
          setChiefComplaintId(response?.data?.id)
        }
        toast.success(response?.message)
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error?.message : 'Unknown error'
      const action = chiefComplaintId ? 'update' : 'submit'
      toast.error(`Failed to ${action} complaints: ${errorMessage}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return <div>Loading complaints...</div>
  }

  return (
    <div className={styles.container}>
      <Typography variant='h6' className={styles.title}>
        Chief Complaints
      </Typography>
      <FormGroup>
        <div className={styles.gridContainer}>
          {complaints?.map((complaint) => (
            <div key={complaint?.id} className={styles.complaintItem}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={complaint?.selected}
                    onChange={handleComplaintChange(complaint?.id)}
                    name={complaint?.name}
                    size='small'
                  />
                }
                label={complaint?.name}
                className={styles.formLabel}
              />
              {complaint?.selected && (
                <div className={styles.durationContainer}>
                  {complaint?.duration === 0 ? (
                    <TextField
                      size='small'
                      type='number'
                      value={
                        complaint?.durationUnit === textHours
                          ? complaint?.duration
                          : ''
                      }
                      onChange={(e) =>
                        handleCustomDurationChange(
                          complaint?.id,
                          e?.target?.value
                        )
                      }
                      placeholder='Enter hours'
                      className={`${styles.durationInput} ${styles.textFieldWhiteBg}`}
                      variant='outlined'
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <span className={styles.durationUnit}>hours</span>
                        ),
                      }}
                    />
                  ) : (
                    <TextField
                      select
                      size='small'
                      value={complaint?.duration || 1}
                      onChange={(e) =>
                        handleDurationChange(
                          complaint?.id,
                          Number(e?.target?.value)
                        )
                      }
                      className={`${styles.durationInput} ${styles.textFieldWhiteBg}`}
                      variant='outlined'
                      fullWidth
                    >
                      {DURATION_OPTIONS?.map((option) => (
                        <MenuItem key={option?.value} value={option?.value}>
                          {option?.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </FormGroup>

      <div className={styles.commentsSection}>
        <Typography variant='subtitle2' className={styles.commentTitle}>
          Additional Comments
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant='outlined'
          value={comment}
          onChange={handleCommentChange}
          placeholder={chiefComplaintsConstants.placeholders.additionalComments}
          className={`${styles.commentField} ${styles.textFieldWhiteBg}`}
        />
        <div className={styles.submitButtonContainer}>
          <Button
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={styles.submitButton}
          >
            {isSubmitting
              ? chiefComplaintsConstants.placeholders.submittingButton
              : chiefComplaintsConstants.placeholders.submitButton}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ChiefComplaints
